import ReactDOM from 'react-dom';
import React, {
  useContext,
  useReducer,
  ReactElement,
} from 'react';
import { Map, Record, OrderedSet } from 'immutable';
import { useCallbackOne, useMemoOne } from 'app/utils/useMemoOne';
import { createElement } from 'app/utils/env';
import { throwingFunction } from 'app/utils/throwingMethods';
import { notify } from 'app/services/errorReporter';
import createErrorType from 'app/utils/createErrorType';

export type SharedInstanceId = string;
export type ClientId = string;
export type ComponentFactory = () => ReactElement;

class RecyclingContinuityError extends createErrorType('RecyclingContinuityError') {
  public constructor(message: string, public readonly sharedInstanceId: SharedInstanceId) {
    super(message);
  }
}

type RegisterFn = (
  sharedInstanceId: SharedInstanceId,
  clientId: ClientId,
) => void;

type UpdateComponentFactoryFn = (
  sharedInstanceId: SharedInstanceId,
  clientId: ClientId,
  factory: ComponentFactory,
) => void;

type UnregisterFn = (
  sharedInstanceId: SharedInstanceId,
  clientId: ClientId,
) => void;

type GetTargetFn = (
  sharedInstanceId: SharedInstanceId,
  clientId: ClientId,
) => HTMLElement | undefined;

type ContextValue = {
  register: RegisterFn;
  updateComponentFactory: UpdateComponentFactoryFn;
  unregister: UnregisterFn;
  getTarget: GetTargetFn;
};

const RecycledComponentsContext = React.createContext<ContextValue>({
  register: throwingFunction,
  updateComponentFactory: throwingFunction,
  unregister: throwingFunction,
  getTarget: throwingFunction,
});

export const useRegisterRecycledComponent = () =>
  useContext(RecycledComponentsContext);

const ManagerState = Record({
  /**
   * Recycled nodes are rendered into target nodes via portals.
   * Targets are created and managed by this component because Portals
   * will only reuse underlying DOM if rendered into the same target node
   */
  targetNodes: Map<SharedInstanceId, HTMLDivElement | undefined>(),
  /**
   * One sharedInstanceId can be linked to multiple components.
   * Components from the links set are served on the LIFO basis
   * i.e. portal is only being rendered to the last registered component
   * under a particular sharedInstanceId
   */
  links: Map<SharedInstanceId, OrderedSet<string> | undefined>(),
  componentFactories: Map<ClientId, ComponentFactory | undefined>(),
});

type State = InstanceType<typeof ManagerState>;

enum ActionType {
  Register = 'register',
  Unregister = 'unregister',
  UpdateComponentFactory = 'updateComponentFactory',
}

type RegisterAction = {
  readonly type: ActionType.Register;
  readonly sharedInstanceId: SharedInstanceId;
  readonly clientId: ClientId;
};

type UnregisterAction = {
  readonly type: ActionType.Unregister;
  readonly sharedInstanceId: SharedInstanceId;
  readonly clientId: ClientId;
};

type UpdateComponentFactoryAction = {
  readonly type: ActionType.UpdateComponentFactory;
  readonly factory: ComponentFactory;
  readonly sharedInstanceId: SharedInstanceId;
  readonly clientId: ClientId;
};

type Action = RegisterAction | UnregisterAction | UpdateComponentFactoryAction;

const registerNodeReducer = (state: State, action: RegisterAction) => {
  const {
    sharedInstanceId,
    clientId,
  } = action;

  return state.withMutations(result => {
    const { targetNodes, links } = result;

    let targetNode = targetNodes.get(sharedInstanceId);

    if (!targetNode) {
      targetNode = createElement('div') as HTMLDivElement;

      result.set(
        'targetNodes',
        targetNodes.set(sharedInstanceId, targetNode),
      );
    }

    let componentLinks = links.get(sharedInstanceId);

    if (!componentLinks) {
      componentLinks = OrderedSet([clientId]);
    } else {
      componentLinks = componentLinks.add(clientId);
    }

    result.set(
      'links',
      links.set(sharedInstanceId, componentLinks),
    );
  });
};

const unregisterNodeReducer = (
  state: State,
  { sharedInstanceId, clientId }: UnregisterAction,
) => {
  const {
    links,
    componentFactories,
  } = state;

  const prevComponentLinks = links.get(sharedInstanceId);

  if (!prevComponentLinks) {
    const error = new RecyclingContinuityError(
      'Attempting to unregister node when no nodes were registered yet',
      sharedInstanceId,
    );
    notify(error);

    return state;
  }

  const updatedComponentLinks = prevComponentLinks.delete(clientId);

  let updatedLinks;

  if (updatedComponentLinks.size > 0) {
    updatedLinks = links.set(
      sharedInstanceId,
      prevComponentLinks.delete(clientId),
    );
  } else {
    updatedLinks = links.delete(sharedInstanceId);
  }

  return state
    .set(
      'links',
      updatedLinks,
    ).set(
      'componentFactories',
      componentFactories.delete(clientId),
    );
};

const updateComponentFactoryReducer = (
  state: State,
  { factory, sharedInstanceId, clientId }: UpdateComponentFactoryAction,
) => {
  const { componentFactories, links } = state;
  const componentLinkExists = links.get(sharedInstanceId)?.has(clientId);

  if (!componentLinkExists) {
    const error = new RecyclingContinuityError(
      'Attempting to set component factory for a component not linked to a shared instance',
      sharedInstanceId,
    );

    notify(error);

    return state;
  }

  const existingFactory = componentFactories.get(clientId);

  if (existingFactory === factory) return state;

  return state.set(
    'componentFactories',
    componentFactories.set(
      clientId,
      factory,
    ),
  );
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.Register:
      return registerNodeReducer(state, action);
    case ActionType.Unregister:
      return unregisterNodeReducer(state, action);
    case ActionType.UpdateComponentFactory:
      return updateComponentFactoryReducer(state, action);
  }
};

const initialState = new ManagerState();

const RecycledComponentsProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    targetNodes,
    componentFactories,
    links,
  } = state;

  const registerNode: RegisterFn = useCallbackOne((
    sharedInstanceId,
    clientId,
  ) => {
    dispatch({
      sharedInstanceId,
      clientId,
      type: ActionType.Register,
    });
  }, []);

  const unregisterNode: UnregisterFn = useCallbackOne((
    sharedInstanceId,
    clientId,
  ) => {
    dispatch({
      sharedInstanceId,
      clientId,
      type: ActionType.Unregister,
    });
  }, []);

  const updateComponentFactory: UpdateComponentFactoryFn = useCallbackOne((
    sharedInstanceId,
    clientId,
    factory,
  ) => {
    dispatch({
      sharedInstanceId,
      clientId,
      factory,
      type: ActionType.UpdateComponentFactory,
    });
  }, []);

  const getTarget: GetTargetFn = useCallbackOne((
    sharedInstanceId: SharedInstanceId,
    clientId: ClientId,
  ) => {
    if (links.get(sharedInstanceId)?.last() !== clientId) return;

    return targetNodes.get(sharedInstanceId);
  }, [links, targetNodes]);

  const recycledNodes = targetNodes.map((target, key) => {
    const activeLinkUuid = links.get(key)?.last();

    if (!activeLinkUuid) {
      return null;
    }

    const componentFactory = componentFactories.get(activeLinkUuid);

    if (!componentFactory || !target) {
      return null;
    }

    return ReactDOM.createPortal(
      componentFactory(),
      target,
      key,
    );
  }).toArray();

  const contextValue: ContextValue = useMemoOne(() => ({
    getTarget,
    updateComponentFactory,
    register: registerNode,
    unregister: unregisterNode,
  }), [getTarget, updateComponentFactory, registerNode, unregisterNode]);

  return (
    <RecycledComponentsContext.Provider
      value={contextValue}
    >
      {children}
      {recycledNodes}
    </RecycledComponentsContext.Provider>
  );
};

export default RecycledComponentsProvider;
